/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
/**
 * Contains all valid units for paper sizes. These are typically either in inches or pixels
 */
export enum PaperUnits {
	Inches = `inches`,
	Pixels = `pixels`,
}

/**
 * Type representing a paper size object.
 */
export interface IPaperSize {
	width: number;
	height: number;
	units: PaperUnits;
}

/**
 * All of the supported paper types (ARCH A to A4, etc)
 */
export enum PaperTypes {
	ArchA = `arch-a`,
	ArchB = `arch-b`,
	ArchC = `arch-c`,
	ArchD = `arch-d`,
	ArchE = `arch-e`,
	ArchE1 = `arch-e1`,
	AnsiA = `ansi-a`,
	AnsiB = `ansi-b`,
	AnsiC = `ansi-c`,
	AnsiD = `ansi-d`,
	AnsiE = `ansi-e`,
	Letter = `letter`,
	Legal = `legal`,
	Tabloid = `tabloid`,
	Ledger = `ledger`,
	A0 = `a0`,
	A1 = `a1`,
	A2 = `a2`,
	A3 = `a3`,
	A4 = `a4`,
	Custom = 'custom',
}

/**
 * Static data containing all of our default supported page sizes.
 */
export const paperSizes: Map<string, IPaperSize> = new Map<string, IPaperSize>([
	[PaperTypes.ArchA, { width: 9, height: 12, units: PaperUnits.Inches }],
	[PaperTypes.ArchB, { width: 12, height: 18, units: PaperUnits.Inches }],
	[PaperTypes.ArchC, { width: 18, height: 24, units: PaperUnits.Inches }],
	[PaperTypes.ArchD, { width: 24, height: 36, units: PaperUnits.Inches }],
	[PaperTypes.ArchE, { width: 36, height: 48, units: PaperUnits.Inches }],
	[PaperTypes.ArchE1, { width: 30, height: 42, units: PaperUnits.Inches }],
	[PaperTypes.AnsiA, { width: 8.5, height: 11, units: PaperUnits.Inches }],
	[PaperTypes.AnsiB, { width: 11, height: 17, units: PaperUnits.Inches }],
	[PaperTypes.AnsiC, { width: 17, height: 22, units: PaperUnits.Inches }],
	[PaperTypes.AnsiD, { width: 22, height: 34, units: PaperUnits.Inches }],
	[PaperTypes.AnsiE, { width: 34, height: 44, units: PaperUnits.Inches }],
	[PaperTypes.Letter, { width: 8.5, height: 11, units: PaperUnits.Inches }],
	[PaperTypes.Legal, { width: 8.5, height: 14, units: PaperUnits.Inches }],
	[PaperTypes.Tabloid, { width: 11, height: 17, units: PaperUnits.Inches }],
	[PaperTypes.Ledger, { width: 17, height: 11, units: PaperUnits.Inches }],
	[PaperTypes.A0, { width: 33.1, height: 46.8, units: PaperUnits.Inches }],
	[PaperTypes.A1, { width: 23.4, height: 33.1, units: PaperUnits.Inches }],
	[PaperTypes.A2, { width: 16.5, height: 23.4, units: PaperUnits.Inches }],
	[PaperTypes.A3, { width: 11.7, height: 16.5, units: PaperUnits.Inches }],
	[PaperTypes.A4, { width: 8.3, height: 11.7, units: PaperUnits.Inches }],
	[PaperTypes.Custom, { width: 0.1, height: 0.1, units: PaperUnits.Inches }],
]);
